import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import StatsStrip from "@/components/StatsStrip";
import About from "@/components/About";
import PracticeAreas from "@/components/PracticeAreas";
import Clients from "@/components/Clients";
import Testimonials from "@/components/Testimonials";
import VisualBand from "@/components/VisualBand";
import FeesEthics from "@/components/FeesEthics";
import Journal from "@/components/Journal";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import AdminPage from "@/components/AdminPage";
import AuthCallback from "@/components/AuthCallback";
import ArticlePage from "@/components/ArticlePage";

const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.4 });
  return (
    <motion.div
      data-testid="scroll-progress-bar"
      style={{ scaleX }}
      className="fixed top-0 inset-x-0 h-[3px] origin-left z-[70] bg-gradient-to-r from-gold/70 via-gold to-gold-hover"
    />
  );
};

function HomePage() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  const scrollTo = (id) => {
    const target = document.getElementById(id);
    if (target) lenisRef.current?.scrollTo(target, { offset: -70 });
  };

  return (
    <div className="bg-navy-950 text-ink min-h-screen" data-testid="app-root">
      <div className="grain-overlay" aria-hidden="true" />
      <ScrollProgress />
      <Header onNavigate={scrollTo} />
      <main>
        <Hero onNavigate={scrollTo} />
        <StatsStrip />
        <About />
        <PracticeAreas />
        <Clients />
        <Testimonials />
        <VisualBand />
        <FeesEthics />
        <Journal />
        <Contact />
      </main>
      <Footer onNavigate={scrollTo} />
      <WhatsAppButton />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/journal/:id" element={<ArticlePage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0A0F42",
            border: "1px solid rgba(154,136,15,0.35)",
            color: "#F5F3EF",
          },
        }}
      />
    </BrowserRouter>
  );
}

export default App;
